import type {
  CompleteInstanceErrorResponse,
  FlowCompletionSummary,
  PublicFlowCompleteResponse,
} from "@repo/shared/types";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import type { NavigateFunction } from "react-router";
import { ApiError, apiErrorCode, apiErrorMessage, postWithToken } from "@/lib/api/_client";
import { getPublicFlowCompletion } from "@/lib/api/public-flow/flow-instance";
import { useSessionToken } from "@/lib/context/public-session-context";
import { invalidateGoalPlatformsAfterRefusedSubmit } from "@/lib/hooks/use-goal-platforms";
import {
  publicFlowCompletionQueryKey,
  setPublicFlowCompletionCache,
} from "./use-public-flow-completion";

type UsePublicFlowSubmitArgs = {
  clientName?: string | null;
  flowLang: string;
  navigate: NavigateFunction;
  /** Called when /complete returns 409 (snapshots stale) so the shell can show the regen banner. */
  onSnapshotStale: () => void;
  /**
   * QL-239 — /complete was refused because the flow moved to preview after
   * this session loaded; the shell shows the preview notice and disables Submit.
   */
  onPreviewBlocked: () => void;
  /** Latest completion from `usePublicFlowCompletion` — drives the D14 auto-clear. */
  completion: FlowCompletionSummary | null;
};

/**
 * Shared submit / completion-view / pdf / signing logic for the public flow
 * (§3.11). Mode-agnostic — Submit lives in the header (shell), so both the
 * single-goal and multi-goal bodies are unaware of it.
 */
export function usePublicFlowSubmit({
  clientName,
  flowLang,
  navigate,
  onSnapshotStale,
  onPreviewBlocked,
  completion,
}: UsePublicFlowSubmitArgs) {
  const sessionToken = useSessionToken();
  const queryClient = useQueryClient();
  const [isCompleting, setIsCompleting] = useState(false);
  const [showCompletionView, setShowCompletionView] = useState(false);
  const [allowPdfGen, setAllowPdfGen] = useState(false);
  const [allowSigning, setAllowSigning] = useState(false);
  // QL-263 — the flow rendered the PDF while submitting (auto-generate on
  // submit): the completion view shows it as ready instead of a generate button.
  const [readyPdf, setReadyPdf] = useState<{ pdfUrl: string; fileName: string } | null>(null);
  // D14 (Follow-up C P1) — flips true on an incomplete preflight / 422, lighting
  // up the progress bar + (P2) tab/widget markers. Auto-clears once nothing is
  // missing, so fixing the last item removes the highlighting without re-submit.
  const [hasAttemptedIncompleteSubmit, setHasAttemptedIncompleteSubmit] = useState(false);

  useEffect(() => {
    if (hasAttemptedIncompleteSubmit && completion && completion.missing.length === 0) {
      setHasAttemptedIncompleteSubmit(false);
    }
  }, [hasAttemptedIncompleteSubmit, completion]);

  const cleanupAndNavigateToThankYou = () => {
    if (clientName) {
      sessionStorage.setItem("publicClientName", clientName);
    }
    sessionStorage.setItem("publicFlowLanguage", flowLang);
    localStorage.removeItem("publicSessionToken");
    localStorage.removeItem("publicSessionToken_for");
    navigate("/p/thank-you");
  };

  const handleSubmit = async () => {
    // Preflight via GET /completion (single source of truth — engine in apps/api).
    // `staleTime: 0` is explicit: fetchQuery uses the QueryClient defaults, not the
    // useQuery override in usePublicFlowCompletion, so without it a fetch within the
    // default window would return stale data and open submit against old state.
    try {
      const fresh = await queryClient.fetchQuery({
        queryKey: publicFlowCompletionQueryKey,
        queryFn: () => getPublicFlowCompletion(sessionToken),
        staleTime: 0,
      });
      if ((fresh?.missing?.length ?? 0) > 0) {
        // Incomplete — light up the progress bar + (P2) markers; do not submit.
        setHasAttemptedIncompleteSubmit(true);
        window.scrollTo({ top: 0, behavior: "smooth" });
        return;
      }
    } catch {
      // Preflight failed (network/auth) — fall through; the POST surfaces the error.
    }

    setIsCompleting(true);
    let result: PublicFlowCompleteResponse;
    try {
      result = await postWithToken<PublicFlowCompleteResponse>(
        "/public/flow/instance/complete",
        sessionToken,
      );
    } catch (err) {
      setIsCompleting(false);
      // QL-546 — the submit may have re-synced goal platforms before refusing.
      if (
        (err instanceof ApiError && err.status === 422) ||
        apiErrorCode(err) === "snapshots_regenerating"
      ) {
        invalidateGoalPlatformsAfterRefusedSubmit(queryClient, {
          isPublic: true,
          flowInstanceId: null,
        });
      }
      // QL-675 — only the stale-snapshot refusal (which queued a rebuild);
      // any other 409 is not a snapshot problem.
      if (apiErrorCode(err) === "snapshots_regenerating") {
        onSnapshotStale();
        window.scrollTo({ top: 0, behavior: "smooth" });
        return;
      }
      if (apiErrorCode(err) === "flow_preview_designers_only") {
        onPreviewBlocked();
        window.scrollTo({ top: 0, behavior: "smooth" });
        return;
      }
      // 422 — backend completion gate (P0). Hydrate the cache so progress/markers
      // reflect server truth immediately, then light up highlighting.
      if (err instanceof ApiError && err.status === 422) {
        const body = err.body as CompleteInstanceErrorResponse | undefined;
        if (body?.requirements) setPublicFlowCompletionCache(queryClient, body.requirements);
        setHasAttemptedIncompleteSubmit(true);
        window.scrollTo({ top: 0, behavior: "smooth" });
        return;
      }
      throw err;
    }

    if (!result.success) {
      setIsCompleting(false);
      cleanupAndNavigateToThankYou();
      return;
    }

    const { allowPdfGen: pdfEnabled, allowSigning: signingEnabled, pdf } = result.data;
    if (pdfEnabled || signingEnabled) {
      setAllowPdfGen(pdfEnabled);
      setAllowSigning(signingEnabled);
      setReadyPdf(pdf ?? null);
      setShowCompletionView(true);
      setIsCompleting(false);
    } else {
      cleanupAndNavigateToThankYou();
    }
  };

  const handleDone = () => {
    cleanupAndNavigateToThankYou();
  };

  const handleGeneratePdf = async () => {
    try {
      const result = await postWithToken<any>("/public/flow/instance/generate-pdf", sessionToken);
      if (!result.success || !result.data) {
        return null;
      }
      return { pdfUrl: result.data.pdfUrl, fileName: result.data.fileName };
    } catch {
      return null;
    }
  };

  const handleSign = async (signatureDataUrl: string) => {
    try {
      const result = await postWithToken<any>(
        "/public/flow/instance/simple-signature",
        sessionToken,
        { signatureDataUrl },
      );
      return { success: result.success, error: result.error };
    } catch (err) {
      // A refusal such as 409 signature_creation_in_progress arrives in the flow's
      // language; show it instead of leaving the signing button stuck (QL-720).
      return { success: false, error: apiErrorMessage(err, "") || undefined };
    }
  };

  return {
    isCompleting,
    showCompletionView,
    allowPdfGen,
    allowSigning,
    /** QL-263 — PDF already rendered on submit, or null when the client generates it. */
    readyPdf,
    /** D14 gating flag — drives the progress bar emphasis + (P2) tab/widget markers. */
    showValidationErrors: hasAttemptedIncompleteSubmit,
    handleSubmit,
    handleDone,
    handleGeneratePdf,
    handleSign,
  };
}
